import { Component, OnInit, OnDestroy, effect } from '@angular/core';
import {
  AbstractControl, AbstractControlOptions,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatCardModule} from '@angular/material/card';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';

import { fetchAuthSession } from 'aws-amplify/auth';
import { Store } from '@ngrx/store';
import { AppState } from '@app/core/store/app.state';
import * as UserAuthActions from '@app/core/store/userAuth/userAuth.actions';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription, take } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule
],
  templateUrl: './change-password-modal.component.html',
  styleUrl: './change-password-modal.component.scss'
})
export class ChangePasswordModalComponent implements OnInit, OnDestroy {

  passwordForm!: FormGroup;
  private subs = new Subscription();

  private changePasswordSuccessSignal
    = toSignal(this.actions$.pipe(ofType(UserAuthActions.ChangePasswordSuccess)));
  private changePasswordFailureSignal
    = toSignal(this.actions$.pipe(ofType(UserAuthActions.ChangePasswordFailure)));

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordModalComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions,
  ) {
    this.setUpSignals();
  }

  ngOnInit() {
    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]).{10,}$/),
        ]
      ],
      confirmPassword: ['', [Validators.required]],
    }, { validator: this.passwordMatchValidator } as AbstractControlOptions);

    this.dialogRef.disableClose = true;
    const escSub = this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.attemptClose();
      }
    });
    this.subs.add(escSub);
  }

  onClose(): void {
    this.attemptClose();
  }

  async onSave(): Promise<void> {
    if (!this.passwordForm.valid) return;

    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    if (!accessToken) return;

    this.store.dispatch(UserAuthActions.ChangePasswordRequested({
      accessToken,
      oldPassword: this.passwordForm.value.currentPassword,
      newPassword: this.passwordForm.value.newPassword,
      confirmPassword: this.passwordForm.value.confirmPassword
    }));
  }

  ngOnDestroy(): void {
    this.passwordForm.reset();
    this.subs.unsubscribe();
  }

  private attemptClose(): void {
    const hasAnyInput = !!this.passwordForm && this.passwordForm.dirty;
    if (!hasAnyInput) {
      this.dialogRef.close();
      return;
    }

    const dialog = this.dialog.open(ConfirmDialogComponent, { disableClose: true });
    dialog.componentInstance.title = 'Unsaved Changes';
    dialog.componentInstance.content = 'You have unsaved changes. Are you sure you want to leave this page?';
    dialog.componentInstance.onConfirm
      .pipe(take(1))
      .subscribe(() => this.dialogRef.close());
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form?.get('newPassword');
    const confirmPassword = form?.get('confirmPassword');

    if (!newPassword || !confirmPassword) { return null; }

    if (newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
    } else {
      const errors = confirmPassword.errors;
      if (errors) {
        delete errors['passwordMismatch'];
        if (!Object.keys(errors).length) { confirmPassword.setErrors(null); }
      }
    }

    return null;
  }

  private resetPasswordForm(): void {
    this.passwordForm.reset();
    Object.keys(this.passwordForm.controls).forEach(key => {
      const ctrl = this.passwordForm.get(key);
      ctrl?.setErrors(null);
    });
  }

  private setUpSignals() {
    effect(() => {
      const changePasswordSuccess = this.changePasswordSuccessSignal();
      if (changePasswordSuccess) {
        this.dialogRef.close();
      }
    });

    effect(() => {
      const changePasswordFailure = this.changePasswordFailureSignal();
      if (changePasswordFailure) {
        this.resetPasswordForm();
      }
    });
  }
}
